import styled from '@emotion/styled'

import type { TypographyColor } from '@polycups/react'

import { Icon } from '@/components/Icon'
import type { TIcon } from '@/components/Icon/Icon'
import { BodyM } from '@/components/Typography'
import { CONFIG } from '@/styles/emotion.config'

export type DefaultProps = {
  content: string
}

export interface ListDefaultProps {
  type: 'ordered' | 'unordered'
  className?: string
  items: { content: string }[]
  color?: TypographyColor
  columns?: 'one' | 'two'
  customBullet?: TIcon
}

const ListDefaultStyles = styled.ul<{
  listStyle: ListDefaultProps['type']
  columns: ListDefaultProps['columns']
  hasCustomBullet: boolean
}>(({ listStyle, columns, hasCustomBullet }) => ({
  ...(listStyle === 'ordered' && {
    counterReset: 'item',
    listStyleType: 'none',
  }),
  '.item': {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  '.item__icon': {
    fontSize: '28px',
  },
  '& li:not(:last-child)': {
    marginBottom: '1rem',
  },

  '& li > *': {
    flex: '1 1 100%',
  },

  ...(!hasCustomBullet && {
    '& li:before': {
      content: `''`,
      display: 'block',
      width: '0.5rem',
      height: '0.5rem',
      borderRadius: '3px',
      flex: '1 0 0.5rem',
      margin: '0.5rem 1.5rem 0.5rem 0.5rem',

      ...(listStyle === 'unordered' && {
        backgroundColor: 'rgba(var(--theme-primary-300))',
      }),

      ...(listStyle === 'ordered' && {
        backgroundColor: 'rgba(var(--theme-primary-300), 0.2)',
        width: '1.5rem',
        height: '1.5rem',
        position: 'absolute',
        top: '0',
        left: '-1.5rem',
        margin: '0',
        display: 'inline-flex',
        fontSize: '0.875rem',
        justifyContent: 'center',
        alignItems: 'center',
        content: 'counter(item)',
        counterIncrement: 'item',
      }),
    },
  }),

  '& li': {
    ...(listStyle === 'unordered' && {
      display: 'flex',
    }),

    ...(listStyle === 'ordered' && {
      display: 'list-item',
      paddingLeft: '1rem',
      marginLeft: '1.5rem',
      position: 'relative',
    }),
    [CONFIG.media.md]: {
      ...(columns === 'two' && {
        display: 'flex',
        position: 'relative',
      }),
    },
  },
  [CONFIG.media.md]: {
    '& li:nth-of-type(odd)': {
      ...(columns === 'two' && {
        clear: 'both',
        float: 'left',
        width: '50%',
      }),
    },
  },
}))

const ListDefault = ({
  className,
  type,
  items,
  color = 'dark-medium',
  columns = 'one',
  customBullet,
}: ListDefaultProps) => {
  const hasCustomBullet = !!customBullet && type === 'unordered'
  return (
    <ListDefaultStyles
      as={type === 'ordered' ? 'ol' : 'ul'}
      listStyle={type}
      className={className}
      columns={columns}
      hasCustomBullet={hasCustomBullet}
    >
      {items &&
        items.map(({ content }, index) => (
          <li key={`ld_item_${index}`}>
            <div className="item">
              {hasCustomBullet && (
                <Icon className="item__icon" color="primary" icon={customBullet} />
              )}
              <BodyM className="item__text" color={color} tag="div" html={content} />
            </div>
          </li>
        ))}
    </ListDefaultStyles>
  )
}

export default ListDefault
